import { BuildingIcon } from "@/Icons/v2/BuildingSelectorIcons/Building";
import { ChevronIcon } from "@/Icons/v2/Chevron";
import { trpc } from "@/Utils/trpc";
import {
  type TimeFilter,
  defaultTimeFilter,
} from "@/components/v2/DatePicker/constants";
import { useTrans } from "@/translations";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";

import { ViewWithLoader } from "../ViewWithLoader";
import { BuildingSelectorPopupView } from "./components/SelectorPopupView";
import type { SavedBuildingView } from "./types";
import { getAllViews, isSameSelection } from "./utils";

type Props = {
  timeFilter?: TimeFilter;
  value: string[] | null;
  onChange: (terminalIds: string[] | null) => void;
  setCurrentViewLabel?: (value: string) => void;
};

export const BuildingSelector: React.FC<Props> = ({
  timeFilter,
  value,
  onChange,
  setCurrentViewLabel,
}) => {
  const { t } = useTrans();
  const [buildingTypesSelected, setBuildingTypesSelected] = useState<
    string[] | null
  >(null);

  const { data: buildingSelections, isLoading: isLoadingBuildingSelections } =
    trpc.config.getBuildingSelections.useQuery();

  const { data: terminalsData, isLoading: isLoadingTerminals } =
    trpc.dwh.terminals.get.useQuery(timeFilter || defaultTimeFilter, {
      queryKeyHashFn: () =>
        [
          "terminals",
          timeFilter?.startTimeISO,
          timeFilter?.endTimeISO,
        ].toString(),
      enabled: !!timeFilter,
    });

  const terminals = useMemo(() => terminalsData || [], [terminalsData]);
  const allIds = useMemo(
    () => terminals.map((term) => term.terminalId),
    [terminals],
  );

  // Report the initial effective selection ("All buildings" resolves to
  // every terminal id) up to the parent as soon as the building list is
  // available, so consumers don't have to special-case a `null` value.
  const hasSentInitialSelection = useRef(false);
  useEffect(() => {
    if (hasSentInitialSelection.current || !allIds.length) return;
    hasSentInitialSelection.current = true;
    onChange(value ?? allIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allIds]);

  // The terminals query waits for the date picker's time filter, so keep
  // loading until that is set and both queries have (re)loaded.
  const isLoading =
    !timeFilter || isLoadingTerminals || isLoadingBuildingSelections;

  const allViews = useMemo(
    () => getAllViews({ terminals, buildingSelections, t }),
    [terminals, buildingSelections, t],
  );

  const currentView = useMemo(
    () => allViews.find((v) => isSameSelection(v.terminalIds, allIds, value)),
    [allViews, allIds, value],
  );

  const viewLabel = useMemo(
    () =>
      currentView
        ? currentView.name
        : t("portfolioPage:customBuildingSelection"),
    [currentView, t],
  );

  const selectedCount = value ? value.length : terminals.length;

  useEffect(() => {
    if (setCurrentViewLabel) {
      setCurrentViewLabel(viewLabel);
    }
  }, [viewLabel, setCurrentViewLabel]);

  const applyView = useCallback(
    (view: SavedBuildingView | null) => {
      onChange(view ? view.terminalIds : null);
      setBuildingTypesSelected(null);
    },
    [onChange],
  );

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <PopoverButton
            disabled={isLoading}
            className="border-grey3 flex cursor-pointer items-center gap-2 rounded-lg border bg-white px-[13px] py-[9px] text-[13px] font-medium disabled:cursor-default max-[1200px]:px-[11px]"
          >
            <ViewWithLoader
              isLoading={isLoading}
              loaderClassName="h-[18px] min-w-[150px] max-[1200px]:min-w-0"
            >
              <BuildingIcon />
              <span className="max-[1200px]:hidden">{viewLabel}</span>
              <span className="text-grey6 text-[11.5px] font-semibold max-[1200px]:hidden">
                {selectedCount}/{terminals.length}
              </span>
              <ChevronIcon open={open} className="max-[1200px]:hidden" />
            </ViewWithLoader>
          </PopoverButton>

          <PopoverPanel
            anchor={{ to: "bottom start", gap: 4.5 }}
            className="border-grey3 z-[10000] w-[320px] rounded-lg border bg-white shadow-lg"
          >
            <BuildingSelectorPopupView
              allViews={allViews}
              currentViewId={currentView?.id}
              applyView={applyView}
              buildingTypesSelected={buildingTypesSelected}
              setBuildingTypesSelected={setBuildingTypesSelected}
              terminals={terminals}
              selectedBuildingIds={value}
              onChange={onChange}
            />
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};
